// Web port of mobile atmosphere.ts — maps live weather (WMO code) + time-of-day
// into a "weather atmosphere" for the web Today Living Sky HUD. The WEATHER drives
// the animation; the TIME-OF-DAY tints the sky (dawn / day / dusk / night).

function condOf(code) {
  if (code == null) return 'cloud';
  if (code <= 1) return 'clear';
  if (code === 2 || code === 3) return 'cloud';
  if (code === 45 || code === 48) return 'mist';
  if (code >= 95) return 'thunder';
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return 'frost';
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return 'rain';
  return 'cloud';
}

function hDec(iso) {
  if (!iso || typeof iso !== 'string' || !iso.includes('T')) return null;
  const t = iso.split('T')[1];
  const [hh, mm] = t.split(':');
  const h = Number(hh) + (Number(mm) || 0) / 60;
  return Number.isFinite(h) ? h : null;
}

function phaseOf(h, sunrise, sunset) {
  if (h < sunrise - 0.5 || h >= sunset + 0.75) return 'night';
  if (h < sunrise + 1) return 'dawn';
  if (h >= sunset - 1) return 'dusk';
  return 'day';
}

const SKY = {
  clear: {
    dawn: { gradient: ['#FCE3C6', '#F3AE84', '#D98C93'], dark: false },
    day: { gradient: ['#CDE8FB', '#8FC3EE', '#5A90CB'], dark: false },
    dusk: { gradient: ['#F7CE92', '#E88E5B', '#8E4B3E'], dark: true },
    night: { gradient: ['#0F1E2C', '#16324A', '#0A1420'], dark: true },
  },
  cloud: {
    dawn: { gradient: ['#E9E0D8', '#CFC7C1', '#ABA6A3'], dark: false },
    day: { gradient: ['#E3E8E3', '#C3CDC2', '#9AA99A'], dark: false },
    dusk: { gradient: ['#D9C6B2', '#B29A8A', '#7A6C63'], dark: false },
    night: { gradient: ['#28323A', '#1C2830', '#121A20'], dark: true },
  },
  mist: {
    dawn: { gradient: ['#F0EAE1', '#DBD5CD', '#C0BCB4'], dark: false },
    day: { gradient: ['#EEF2EC', '#D8E0D7', '#BCC8BB'], dark: false },
    dusk: { gradient: ['#E2D6C5', '#C9BBAA', '#9C8E82'], dark: false },
    night: { gradient: ['#2A3038', '#1E242B', '#141920'], dark: true },
  },
  rain: {
    dawn: { gradient: ['#C3C2CC', '#9895A6', '#6B697A'], dark: true },
    day: { gradient: ['#B7C6D1', '#8AA1B0', '#566B78'], dark: true },
    dusk: { gradient: ['#B7A6A4', '#8C797C', '#544A50'], dark: true },
    night: { gradient: ['#1C2733', '#141C26', '#0C1119'], dark: true },
  },
  thunder: {
    dawn: { gradient: ['#8B8F9C', '#5C6470', '#33383F'], dark: true },
    day: { gradient: ['#8E97A3', '#5E6875', '#33393F'], dark: true },
    dusk: { gradient: ['#6E6570', '#4A4450', '#2A2730'], dark: true },
    night: { gradient: ['#161A22', '#0F131A', '#080A0F'], dark: true },
  },
  frost: {
    dawn: { gradient: ['#EAEFF2', '#CFDBE1', '#A6BAC5'], dark: false },
    day: { gradient: ['#E6EEF2', '#C6D7E0', '#96AFBD'], dark: false },
    dusk: { gradient: ['#DCE0E5', '#BEC6CE', '#8E99A5'], dark: false },
    night: { gradient: ['#20303A', '#17242E', '#101A22'], dark: true },
  },
};

const COND_LABEL = { clear: 'Sunny', cloud: 'Cloudy', mist: 'Misty', rain: 'Rainy', thunder: 'Stormy', frost: 'Frosty' };

export function atmosphereFor(weatherRaw, hour, frostLevel) {
  const code = weatherRaw?.current?.weather_code ?? weatherRaw?.daily?.weather_code?.[0];
  const sunrise = hDec(weatherRaw?.daily?.sunrise?.[0]) ?? 6.5;
  const sunset = hDec(weatherRaw?.daily?.sunset?.[0]) ?? 18.5;
  const phase = phaseOf(hour, sunrise, sunset);

  let cond = condOf(code);
  if (frostLevel === 'high' && phase === 'dawn' && (cond === 'clear' || cond === 'cloud')) {
    cond = 'frost';
  }

  let mood;
  if (cond === 'clear') mood = phase === 'night' ? 'night' : phase === 'day' ? 'sun' : 'dusk';
  else if (cond === 'rain') mood = 'rain';
  else if (cond === 'thunder') mood = 'thunder';
  else if (cond === 'frost') mood = 'frost';
  else if (cond === 'mist') mood = 'mist';
  else mood = 'cloud';

  const sky = SKY[cond][phase];
  const dark = sky.dark;
  const textColor = dark ? '#FBFDFF' : '#14324A';
  const subTextColor = dark ? 'rgba(251,253,255,0.84)' : 'rgba(20,50,74,0.72)';

  const wind = Number(weatherRaw?.current?.wind_speed_10m ?? weatherRaw?.daily?.wind_speed_10m_max?.[0] ?? 0) || 0;
  const windApplicable = mood === 'sun' || mood === 'dusk' || mood === 'cloud' || mood === 'mist';
  const windy = wind >= 25 && windApplicable;

  const todWord = phase === 'dawn' ? 'dawn' : phase === 'night' ? 'night' : hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  let caption =
    cond === 'clear' && phase === 'night' ? 'Starry night'
    : cond === 'clear' && phase === 'dusk' ? 'Golden hour'
    : cond === 'clear' && phase === 'dawn' ? 'First light'
    : cond === 'clear' ? `Clear ${todWord}`
    : cond === 'cloud' ? `Overcast ${todWord}`
    : cond === 'mist' ? `Misty ${todWord}`
    : cond === 'rain' ? `Wet ${todWord}`
    : cond === 'thunder' ? `Stormy ${todWord}`
    : `Frosty ${todWord}`;
  if (windy) caption += ' · breezy';

  const condition = cond === 'clear' && phase === 'night' ? 'Clear' : COND_LABEL[cond];
  const sheen = dark ? 'rgba(255,255,255,0.28)' : 'rgba(255,246,214,0.5)';

  return { mood, phase, cond, gradient: sky.gradient, sheen, caption, condition, dark, textColor, subTextColor, windy, windSpeed: Math.round(wind) };
}

// Frost level from tonight's forecast low (deg C) — mirrors the mobile vitals input.
export function frostLevelFrom(weatherRaw) {
  const lo = weatherRaw?.daily?.temperature_2m_min?.[0];
  if (lo == null) return 'none';
  if (lo <= 0) return 'high';
  if (lo <= 2) return 'medium';
  return 'none';
}
